import { useState, useEffect, useCallback, useRef } from "react";
import { useChatPersistence } from "@/hooks/useChatPersistence";
import { supabase } from "@/integrations/supabase/client";
import { soundManager } from "@/utils/sounds";
import { clearResumeBuilderFormDrafts } from "@/lib/formDraftStorage";
import { useOnlineStatus } from "@/hooks/useOnlineStatus";

interface Message {
  id: string;
  content: string;
  isUser: boolean;
  timestamp: Date;
  formType?: 'personal' | 'work' | 'education';
  isLoading?: boolean;
  action?: 'refreshChat' | 'uploadResume';
}

interface ResumeData {
  selectedTemplateId?: string;
  templateId?: string;
  recommendedTemplate?: { templateId: string; reason: string };
  personalInfo: {
    firstName: string;
    middleName?: string;
    lastName: string;
    fullName?: string;
    email: string;
    location: string;
    city?: string;
    mobile: string;
    phone?: string;
    linkedin?: string;
    portfolio?: string;
    jobTitle?: string;
    title?: string;
  };
  summary: string;
  skills: string[];
  technicalSkills?: string[];
  softSkills?: string[];
  languages: string[];
  certifications: string[];
  workExperience: Array<{
    company: string;
    companyName?: string;
    position: string;
    jobTitle?: string;
    startMonth?: string;
    startYear: string;
    endMonth?: string;
    endYear: string;
    location: string;
    description: string[];
    noNumbers?: boolean;
  }>;
  education: Array<{
    institution: string;
    school?: string;
    degree: string;
    major: string;
    field?: string;
    startMonth?: string;
    startYear: string;
    endMonth?: string;
    endYear: string;
    location: string;
    awards?: string[];
    grade?: string;
    grades?: string;
    projects?: string[];
    publications?: string[];
  }>;
  references: Array<{
    name: string;
    position: string;
    company?: string;
    email: string;
    phone?: string;
  }>;
  projects?: any[];
  awards?: any[];
  additionalInfo?: any;
  rawMissingInfo?: Record<string, string>;
  targetJobDescription?: string;
  atsAnalysis?: any;
  tailoringInstructions?: any;
  tailoringApplied?: boolean;
}

interface ATSAnalysis {
  score: number;
  matchedKeywords: string[];
  missingKeywords: string[];
  skillGaps: { skill: string; suggestion: string }[];
  improvements: { section: string; suggestion: string }[];
  overallFeedback: string;
}

const createMessageId = () => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }

  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

const STOP_BUILD_MESSAGE = "No problem, I've stopped this resume build. To start fresh, click Refresh Chat.";

const isStopRequest = (message: string) => {
  const normalized = message.toLowerCase().replace(/[^\w\s']/g, ' ').replace(/\s+/g, ' ').trim();
  const stopPhrases = [
    'nevermind',
    'never mind',
    'forget it',
    'stop building',
    'stop this',
    'end this',
    'cancel this',
    'quit this',
    'i want to stop',
    'i want to cancel',
    "i don't want to continue",
    'do not continue',
    "don't continue",
  ];
  const stopWords = ['stop', 'end', 'cancel', 'quit', 'abort', 'exit'];
  const words = normalized.split(' ');

  return stopWords.some((word) => words.includes(word)) ||
    stopPhrases.some((phrase) => normalized === phrase || normalized.includes(phrase));
};

const buildResumeDataFromParsed = (parsedData: any, previous: Partial<ResumeData> = {}): Partial<ResumeData> => {
  const fullName = parsedData.fullName || previous.personalInfo?.fullName || '';
  const nameParts = fullName.trim().split(/\s+/).filter(Boolean);
  const technicalSkills = uniqueStrings([
    ...(Array.isArray(parsedData.skills?.technical) ? parsedData.skills.technical : []),
    ...(Array.isArray(parsedData.skills?.technicalSkills) ? parsedData.skills.technicalSkills : []),
    ...(Array.isArray(parsedData.technicalSkills) ? parsedData.technicalSkills : []),
  ]);
  const softSkills = uniqueStrings([
    ...(Array.isArray(parsedData.skills?.soft) ? parsedData.skills.soft : []),
    ...(Array.isArray(parsedData.skills?.softSkills) ? parsedData.skills.softSkills : []),
    ...(Array.isArray(parsedData.softSkills) ? parsedData.softSkills : []),
  ]);
  const parsedSkillList = Array.isArray(parsedData.skills) ? parsedData.skills : [];
  const extractedTextPreview = parsedData._extractedTextPreview || parsedData.extractedTextPreview || '';
  const additionalInfo = [
    parsedData.additionalInfo,
    extractedTextPreview ? `Uploaded resume extracted text:\n${extractedTextPreview}` : '',
    previous.additionalInfo,
  ].filter(Boolean).join('\n\n') || undefined;

  return {
    ...previous,
    personalInfo: {
      ...(previous.personalInfo || {}),
      firstName: nameParts[0] || previous.personalInfo?.firstName || '',
      lastName: nameParts.slice(1).join(' ') || previous.personalInfo?.lastName || '',
      fullName,
      email: parsedData.email || previous.personalInfo?.email || '',
      location: parsedData.city || parsedData.location || previous.personalInfo?.location || '',
      city: parsedData.city || previous.personalInfo?.city || '',
      mobile: parsedData.phone || previous.personalInfo?.mobile || '',
      phone: parsedData.phone || previous.personalInfo?.phone || '',
      linkedin: parsedData.linkedinUrl || previous.personalInfo?.linkedin || '',
      portfolio: parsedData.portfolioUrl || previous.personalInfo?.portfolio || '',
      jobTitle: parsedData.jobTitle || previous.personalInfo?.jobTitle || '',
      title: parsedData.jobTitle || previous.personalInfo?.title || '',
    },
    summary: parsedData.summary || previous.summary || '',
    skills: uniqueStrings([...parsedSkillList, ...technicalSkills, ...softSkills]),
    technicalSkills,
    softSkills,
    languages: parsedData.languages || previous.languages || [],
    certifications: parsedData.certifications || previous.certifications || [],
    workExperience: parsedData.workExperience || previous.workExperience || [],
    education: parsedData.education || previous.education || [],
    references: parsedData.references || previous.references || [],
    projects: parsedData.projects || previous.projects || [],
    awards: parsedData.awards || previous.awards || [],
    additionalInfo,
  };
};

const uniqueStrings = (items: any[]): string[] => Array.from(new Set(
  items
    .filter((item): item is string => typeof item === 'string')
    .map((item) => item.trim())
    .filter(Boolean)
));

const ATS_STOP_WORDS = new Set([
  'about', 'above', 'after', 'again', 'against', 'also', 'and', 'any', 'are', 'can', 'candidate',
  'company', 'description', 'does', 'each', 'equal', 'experience', 'from', 'have', 'ideal', 'include',
  'including', 'into', 'job', 'looking', 'must', 'our', 'please', 'preferred', 'requirements', 'role',
  'should', 'that', 'the', 'their', 'this', 'through', 'using', 'with', 'work', 'will', 'you', 'your'
]);

const stringifyResumeForATS = (value: unknown): string => {
  if (!value) return '';
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  if (Array.isArray(value)) {
    return value.map(stringifyResumeForATS).join(' ');
  }
  if (typeof value === 'object') {
    return Object.values(value as Record<string, unknown>).map(stringifyResumeForATS).join(' ');
  }
  return '';
};

const extractATSKeywords = (text: string): string[] => {
  const normalized = text
    .toLowerCase()
    .replace(/[^a-z0-9+#.\s-]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  const words = normalized
    .split(' ')
    .map((word) => word.replace(/^-+|-+$/g, '').trim())
    .filter((word) => word.length >= 3 && !ATS_STOP_WORDS.has(word));

  return uniqueStrings(words).slice(0, 80);
};

const normalizeATSAnalysis = (analysis: any): ATSAnalysis | null => {
  if (!analysis || typeof analysis !== 'object') return null;

  const score = Number(analysis.score);
  if (!Number.isFinite(score)) return null;

  return {
    score: Math.max(0, Math.min(100, Math.round(score))),
    matchedKeywords: uniqueStrings(Array.isArray(analysis.matchedKeywords) ? analysis.matchedKeywords : []),
    missingKeywords: uniqueStrings(Array.isArray(analysis.missingKeywords) ? analysis.missingKeywords : []),
    skillGaps: Array.isArray(analysis.skillGaps)
      ? analysis.skillGaps
        .map((gap: any) => ({
          skill: String(gap?.skill || '').trim(),
          suggestion: String(gap?.suggestion || '').trim()
        }))
        .filter((gap: any) => gap.skill && gap.suggestion)
      : [],
    improvements: Array.isArray(analysis.improvements)
      ? analysis.improvements
        .map((improvement: any) => ({
          section: String(improvement?.section || '').trim(),
          suggestion: String(improvement?.suggestion || '').trim()
        }))
        .filter((improvement: any) => improvement.section && improvement.suggestion)
      : [],
    overallFeedback: String(analysis.overallFeedback || 'Your resume has been compared with the job description. Review the keyword matches and suggested improvements before choosing a template.').trim(),
  };
};

const createLocalATSAnalysis = (jobDescription: string, resumeData: Partial<ResumeData>): ATSAnalysis => {
  const jobKeywords = extractATSKeywords(jobDescription);
  const resumeKeywords = new Set(extractATSKeywords(stringifyResumeForATS(resumeData)));
  const matchedKeywords = jobKeywords.filter((keyword) => resumeKeywords.has(keyword)).slice(0, 10);
  const missingKeywords = jobKeywords.filter((keyword) => !resumeKeywords.has(keyword)).slice(0, 7);
  const matchRatio = jobKeywords.length > 0 ? matchedKeywords.length / Math.min(jobKeywords.length, 40) : 0;
  const hasExperience = Array.isArray(resumeData.workExperience) && resumeData.workExperience.length > 0;
  const hasSkills = Array.isArray(resumeData.skills) && resumeData.skills.length > 0;
  const score = Math.max(35, Math.min(92, Math.round(matchRatio * 70 + (hasExperience ? 12 : 0) + (hasSkills ? 10 : 0))));

  return {
    score,
    matchedKeywords: matchedKeywords.length > 0 ? matchedKeywords : ['resume content', 'professional experience'],
    missingKeywords: missingKeywords.length > 0 ? missingKeywords : ['role-specific keywords'],
    skillGaps: missingKeywords.slice(0, 4).map((keyword) => ({
      skill: keyword,
      suggestion: `Add truthful evidence of ${keyword} only if it matches your real experience, tools, or training.`
    })),
    improvements: [
      {
        section: 'Summary',
        suggestion: 'Mirror the target role and include the strongest matching skills from the job description.'
      },
      {
        section: 'Work Experience',
        suggestion: 'Add relevant job-description keywords into achievement bullets where they truthfully fit.'
      },
      {
        section: 'Skills',
        suggestion: 'Prioritize tools, technologies, and competencies requested in the job description.'
      }
    ],
    overallFeedback: 'Your enhanced resume has been compared with the job description using a local keyword-match fallback. Review the missing keywords and only apply improvements that truthfully match your experience.'
  };
};

const mergeFormDataIntoResume = (
  previous: Partial<ResumeData>,
  formData: any,
  formType: 'personal' | 'work' | 'education'
): Partial<ResumeData> => {
  const prev: any = previous || {};

  if (formType === 'personal') {
    const fullName = formData.fullName || prev.personalInfo?.fullName || '';
    const nameParts = fullName.trim().split(/\s+/).filter(Boolean);
    const phone = [formData.dialCode, formData.phoneNumber].filter(Boolean).join(' ').trim();
    const location = [formData.city, formData.country].filter(Boolean).join(', ');

    return {
      ...prev,
      ...formData,
      personalInfo: {
        ...(prev.personalInfo || {}),
        firstName: prev.personalInfo?.firstName || nameParts[0] || '',
        lastName: prev.personalInfo?.lastName || nameParts.slice(1).join(' ') || '',
        fullName,
        email: formData.email || prev.personalInfo?.email || '',
        location: location || prev.personalInfo?.location || '',
        city: formData.city || prev.personalInfo?.city || '',
        mobile: phone || formData.phoneNumber || prev.personalInfo?.mobile || '',
        phone: phone || formData.phoneNumber || prev.personalInfo?.phone || '',
        linkedin: formData.linkedinUrl || prev.personalInfo?.linkedin || '',
        portfolio: formData.portfolioUrl || prev.personalInfo?.portfolio || '',
        jobTitle: formData.jobTitle || prev.personalInfo?.jobTitle || '',
        title: formData.jobTitle || prev.personalInfo?.title || '',
      },
    };
  }

  if (formType === 'work') {
    const workExperience = Array.isArray(formData)
      ? formData
      : Array.isArray(formData?.workExperience)
        ? formData.workExperience
        : prev.workExperience || [];

    return {
      ...prev,
      ...(Array.isArray(formData) ? {} : formData),
      workExperience,
    };
  }

  const technicalSkills = Array.isArray(formData?.technicalSkills) ? formData.technicalSkills : [];
  const softSkills = Array.isArray(formData?.softSkills) ? formData.softSkills : [];
  const existingSkills = Array.isArray(prev.skills) ? prev.skills : [];

  return {
    ...prev,
    ...formData,
    skills: uniqueStrings([...existingSkills, ...technicalSkills, ...softSkills]),
    references: formData?.references || formData?.referees || prev.references || [],
  };
};

export function useChat(userName?: string) {
  const displayName = userName || 'there';
  const isOnline = useOnlineStatus();

  const [messages, setMessages] = useState<Message[]>([
    {
      id: "welcome",
      content: `Hi ${displayName}, let's build you the perfect resume. Are you ready?`,
      isUser: false,
      timestamp: new Date(),
    },
  ]);
  const [isLoading, setIsLoading] = useState(false);
  const [resumeData, setResumeData] = useState<Partial<ResumeData>>({});
  const resumeDataRef = useRef<Partial<ResumeData>>({});
  const [currentStep, setCurrentStep] = useState<string>(() => (
    localStorage.getItem('resumeBuilder.currentStep') || "greeting"
  ));
  const [isConversationComplete, setIsConversationComplete] = useState(false);

  const [showFormType, setShowFormType] = useState<'personal' | 'work' | 'education' | null>(() => {
    const saved = localStorage.getItem('showFormType');
    return saved === 'personal' || saved === 'work' || saved === 'education' ? saved : null;
  });

  const [waitingForChoice, setWaitingForChoice] = useState<boolean>(() => {
    const saved = localStorage.getItem('waitingForChoice');
    return saved === 'true' ? true : false;
  });
  const [uploadedResumeData, setUploadedResumeData] = useState<any>(null);
  const [showTemplateSelection, setShowTemplateSelection] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [isUploadedResume, setIsUploadedResume] = useState(false);

  const [showJobDescriptionInput, setShowJobDescriptionInput] = useState(() => (
    localStorage.getItem('resumeBuilder.showJobDescriptionInput') === 'true'
  ));
  const [showATSPreview, setShowATSPreview] = useState(false);
  const [atsAnalysis, setAtsAnalysis] = useState<ATSAnalysis | null>(null);
  const [analyzingJD, setAnalyzingJD] = useState(false);
  const [missingInfoFields, setMissingInfoFields] = useState<string[]>([]);

  useEffect(() => {
    resumeDataRef.current = resumeData;
  }, [resumeData]);
  const [recommendedTemplate, setRecommendedTemplate] = useState<{ id: string; reason: string } | null>(null);

  useEffect(() => {
    const restoredAnalysis = normalizeATSAnalysis((resumeData as any)?.atsAnalysis);
    if (restoredAnalysis && !atsAnalysis) {
      setAtsAnalysis(restoredAnalysis);
    }
  }, [resumeData, atsAnalysis]);

  // Update welcome message when userName changes
  useEffect(() => {
    if (currentStep === 'greeting' && messages.length === 1 && messages[0].id === 'welcome') {
      setMessages([{
        id: "welcome",
        content: `Hi ${displayName}, let's build you the perfect resume. Are you ready?`,
        isUser: false,
        timestamp: new Date(),
      }]);
    }
  }, [displayName]);

  useEffect(() => {
    localStorage.setItem('waitingForChoice', waitingForChoice.toString());
  }, [waitingForChoice]);

  useEffect(() => {
    localStorage.setItem('showFormType', showFormType || '');
  }, [showFormType]);

  useEffect(() => {
    localStorage.setItem('resumeBuilder.currentStep', currentStep);
  }, [currentStep]);

  useEffect(() => {
    localStorage.setItem('resumeBuilder.showJobDescriptionInput', showJobDescriptionInput.toString());
  }, [showJobDescriptionInput]);

  const { clearChatSession } = useChatPersistence({
    messages,
    resumeData,
    currentStep,
    isConversationComplete,
    showTemplateSelection,
    showJobDescriptionInput,
    showATSPreview,
    showFormType,
    waitingForChoice,
    setMessages,
    setResumeData,
    setCurrentStep,
    setIsConversationComplete,
    setShowTemplateSelection,
    setShowJobDescriptionInput,
    setShowATSPreview,
    setShowFormType,
    setWaitingForChoice,
  });

  const addMessage = useCallback((content: string, isUser: boolean) => {
    const newMessage: Message = {
      id: createMessageId(),
      content,
      isUser,
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, newMessage]);
  }, []);

  const addActionMessage = useCallback((content: string, action: Message['action']) => {
    const newMessage: Message = {
      id: createMessageId(),
      content,
      isUser: false,
      timestamp: new Date(),
      action,
    };
    setMessages((prev) => [...prev, newMessage]);
  }, []);

  const addLoadingMessage = useCallback((content: string) => {
    const newMessage: Message = {
      id: createMessageId(),
      content,
      isUser: false,
      timestamp: new Date(),
      isLoading: true
    };
    setMessages((prev) => [...prev, newMessage]);
    return newMessage.id;
  }, []);

  const removeLoadingMessage = useCallback((messageId: string) => {
    setMessages((prev) => prev.filter(msg => msg.id !== messageId));
  }, []);

  const callOpenAI = useCallback(async (userMessage: string, includeUploadedData: boolean = false, mode: 'collect' | 'enhance' = 'collect') => {
    try {
      if (!isOnline) {
        return "Offline build mode is active. I can help you continue with the manual resume forms, but AI responses will resume when you're back online.";
      }

      if (!userMessage || typeof userMessage !== 'string') {
        throw new Error('Invalid message');
      }

      if (userMessage.length > 2000) {
        throw new Error('Message too long');
      }

      const restartWords = ['restart', 'start over', 'begin again', 'reset', 'new resume', 'start fresh'];
      if (restartWords.some(word => userMessage.toLowerCase().includes(word))) {
        return "I understand you'd like to restart, but I need you to continue with our current conversation. You can't restart mid-process - only continue or move forward. Let's keep building your resume from where we are.";
      }

      const finishWords = ['finish', 'done', 'completed', 'complete'];
      const hasFinishWord = finishWords.some(word => userMessage.toLowerCase().includes(word));

      if (hasFinishWord && mode === 'collect') {
        setIsConversationComplete(true);
        return "Perfect! I have all the information needed. Ready to enhance your resume with professional language and action verbs.";
      }

      let messageWithContext = userMessage;
      if (includeUploadedData && uploadedResumeData) {
        messageWithContext = `[Context: User has uploaded their resume with following parsed data: ${JSON.stringify(uploadedResumeData)}]\n\nUser's message: ${userMessage}`;
      }

      const conversationHistory = messages.slice(-6).map(msg => ({
        role: msg.isUser ? 'user' : 'assistant',
        content: msg.content
      }));

      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('timeout')), 45000)
      );

      const apiCall = supabase.functions.invoke('chat-with-ai', {
        body: {
          message: messageWithContext,
          conversationHistory,
          mode,
          uploadedResumeData: includeUploadedData ? uploadedResumeData : undefined
        }
      });

      const result = await Promise.race([apiCall, timeoutPromise]);
      const { data, error } = result as { data: any, error: any };

      if (error) {
        console.error('Edge function error:', error);
        throw new Error('Failed to get AI response');
      }

      if (!data?.success) {
        throw new Error(data?.error || 'AI service error');
      }

      return data.response || "I'm sorry, I didn't understand that. Could you try again?";
    } catch (error: any) {
      console.error('AI chat error:', error);
      if (error.message === 'timeout') {
        return "I'm helping many users now. Can we pause for 1 minute?";
      }
      return "I'm having trouble connecting right now. Report this to our team";
    }
  }, [isOnline, messages, uploadedResumeData]);

  // Detect if user shows interest to start
  const detectInterest = useCallback((message: string): 'interested' | 'not_interested' | 'create' | 'upload' => {
    const lower = message.toLowerCase();

    const uploadKeywords = ['upload', 'existing', 'optimize', 'improve', 'update', 'old resume', 'have a resume', 'already have', 'my resume'];
    const createKeywords = ['create', 'build', 'new', 'make', 'write', 'from scratch'];
    const interestedKeywords = ['yes', 'ready', "let's go", 'sure', 'yeah', 'yep', 'absolutely', 'of course', 'start', 'begin', 'ok', 'okay', 'go ahead', 'let\'s do it', 'definitely', 'please', 'help me'];
    const notInterestedKeywords = ['no', 'not sure', 'not ready', 'what is this', 'what do you do', 'how does this work', 'tell me more', 'explain', 'why', 'later', 'maybe'];

    const hasUpload = uploadKeywords.some(k => lower.includes(k));
    const hasCreate = createKeywords.some(k => lower.includes(k));
    const hasInterested = interestedKeywords.some(k => lower.includes(k));
    const hasNotInterested = notInterestedKeywords.some(k => lower.includes(k));

    if (hasUpload && !hasCreate) return 'upload';
    if (hasCreate && !hasUpload) return 'create';
    if (hasInterested && !hasNotInterested) return 'interested';
    if (hasNotInterested) return 'not_interested';
    // Default: if short affirmative message, treat as interested
    if (lower.length < 20 && !hasNotInterested) return 'interested';
    return 'not_interested';
  }, []);

  const beginJobTailoring = useCallback((message = "Before we finalize, do you have a specific job you'd like to tailor this resume for?") => {
    addMessage(message, false);
    setShowJobDescriptionInput(true);
    setShowTemplateSelection(false);
    setShowATSPreview(false);
    setCurrentStep('job-tailoring');
    setIsLoading(false);
    setShowFormType(null);
  }, [addMessage]);

  const generateResume = useCallback(async (resumeOverride?: Partial<ResumeData>) => {
    setIsLoading(true);
    try {
      if (!isOnline) {
        addMessage("Offline build mode is active, so AI enhancement is paused. Your manual resume data is ready for template selection and download.", false);
        return false;
      }

      addMessage("Enhancing your resume with AI...", false);
      const resumePayload = resumeOverride && Object.keys(resumeOverride).length > 0
        ? resumeOverride
        : resumeDataRef.current;

      const conversationHistory = messages.map(msg => ({
        role: msg.isUser ? 'user' : 'assistant',
        content: msg.content
      }));

      const { data, error } = await supabase.functions.invoke('chat-with-ai', {
        body: {
          mode: 'generate',
          resumeData: resumePayload,
          conversationHistory
        }
      });

      if (error) {
        console.error('Resume enhancement error:', error);
        throw new Error('Failed to enhance resume');
      }

      if (data?.success && data?.resumeData) {
        const templateId = (resumePayload as any)?.selectedTemplateId || (resumePayload as any)?.templateId;
        const enhancedResume = templateId
          ? { ...data.resumeData, selectedTemplateId: templateId, templateId }
          : data.resumeData;
        const aiRecommendation = data.resumeData?.recommendedTemplate;
        if (
          aiRecommendation?.templateId &&
          ['ats-optimized', 'executive', 'professional-classic'].includes(aiRecommendation.templateId)
        ) {
          setRecommendedTemplate({
            id: aiRecommendation.templateId,
            reason: aiRecommendation.reason || 'Recommended based on your resume details',
          });
        }

        // Update resume data with AI-enhanced version
        resumeDataRef.current = enhancedResume;
        setResumeData(enhancedResume);
        addMessage("Perfect! Your resume has been enhanced with professional language and action verbs.", false);
        return true;
      } else {
        addMessage("Resume processing completed. Your data is ready for download.", false);
        return false;
      }
    } catch (error) {
      console.error('Resume generation error:', error);
      addMessage("Resume enhancement completed. You can proceed with download.", false);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [isOnline, messages, addMessage]);

  const optimizeResumeAndShowTemplate = useCallback(async (resumeOverride?: Partial<ResumeData>) => {
    // Add loading message for optimization
    const loadingMessageId = createMessageId();
    setMessages(prev => [...prev, {
      id: loadingMessageId,
      content: "Optimizing your resume with professional language",
      isUser: false,
      timestamp: new Date(),
      isLoading: true
    }]);

    try {
      await generateResume(resumeOverride);

      setTimeout(() => {
        setMessages(prev => prev.filter(msg => msg.id !== loadingMessageId));
        addMessage("Perfect! Your resume has been optimized with professional language and ATS keywords.", false);

        setTimeout(() => {
          beginJobTailoring();
        }, 1000);
      }, 1000);
    } catch (error) {
      console.error('Resume optimization failed:', error);
      setTimeout(() => {
        setMessages(prev => prev.filter(msg => msg.id !== loadingMessageId));
        addMessage("Resume optimization completed.", false);
        setTimeout(() => {
          beginJobTailoring();
        }, 600);
      }, 1000);
    }
  }, [generateResume, addMessage, beginJobTailoring]);

  const processUserMessage = useCallback(async (userMessage: string) => {
    addMessage(userMessage, true);
    setIsLoading(true);

    try {
      if (isStopRequest(userMessage) || currentStep === 'stopped') {
        setCurrentStep('stopped');
        setIsConversationComplete(false);
        setShowFormType(null);
        setWaitingForChoice(false);
        setShowTemplateSelection(false);
        setShowJobDescriptionInput(false);
        setShowATSPreview(false);
        setMissingInfoFields([]);
        setIsLoading(false);
        addActionMessage(STOP_BUILD_MESSAGE, 'refreshChat');
        return;
      }

      // Check if this is handling missing info after resume upload
      const isMissingInfoStep = currentStep === 'resume-upload-missing-info' && uploadedResumeData !== null;

      if (isMissingInfoStep) {
        // Check if user wants to skip providing missing details
        const skipKeywords = ['skip', 'continue', 'no', "that's all", "looks good", "proceed", "let's continue"];
        const wantsToSkip = skipKeywords.some(keyword => userMessage.toLowerCase().includes(keyword));
        
        if (wantsToSkip) {
          addMessage("No problem! I'll optimize the resume using the information we have.", false);

          setMissingInfoFields([]);
          setTimeout(() => {
            optimizeResumeAndShowTemplate(resumeDataRef.current);
          }, 600);
          return;
        }
        
        addMessage("Processing the additional information you provided...", false);

        // Use AI to extract the missing information from user's response
        const extractResponse = await supabase.functions.invoke('chat-with-ai', {
          body: {
            message: `The user uploaded a resume but some information was missing for optimization. They just provided: "${userMessage}". 

Original parsed resume data: ${JSON.stringify(uploadedResumeData)}

Please extract any missing information (fullName, email, phone, workExperience, education) from their response and return it as JSON. If no new information is provided, return an empty object. Only return valid JSON.`,
            conversationHistory: messages.slice(-2).map(msg => ({
              role: msg.isUser ? 'user' : 'assistant',
              content: msg.content
            })),
            mode: 'collect'
          }
        });

        let extractedInfo = {};
        try {
          const response = extractResponse?.data?.response || '{}';
          // Try to extract JSON from the response
          const jsonMatch = response.match(/\{[\s\S]*\}/);
          if (jsonMatch) {
            extractedInfo = JSON.parse(jsonMatch[0]);
          }
        } catch (e) {
          console.error('Failed to parse extracted info:', e);
        }

        // Merge extracted info with original data
        const { normalizeParsedResumeData, validateParsedResumeData } = await import('@/utils/validation');
        const updatedData = normalizeParsedResumeData({ ...uploadedResumeData, ...extractedInfo });
        const updatedResumeForGeneration = buildResumeDataFromParsed(updatedData, resumeDataRef.current);
        
        // Re-validate the updated data
        const reValidation = validateParsedResumeData(updatedData);
        
        // Update the uploaded data with whatever we got
        setUploadedResumeData(updatedData);
        
        // Update resume data with new information
        resumeDataRef.current = updatedResumeForGeneration;
        setResumeData(updatedResumeForGeneration);
        
        if (!reValidation.isValid && reValidation.missingFields.length > 0) {
          // Still missing some fields, offer to continue
          const missingFieldNames = reValidation.missingFields.map(field => {
            const fieldMap: { [key: string]: string } = {
              fullName: "full name",
              email: "email address", 
              phone: "phone number",
              workExperience: "work experience",
              education: "education details"
            };
            return fieldMap[field] || field;
          });
          
          addMessage(`Thanks for the information! We're still missing: ${missingFieldNames.join(', ')}, but we can continue with optimization if you'd like.`, false);
          
          setTimeout(() => {
            addMessage("Would you like to provide more details or continue with what we have?", false);
            setIsLoading(false);
          }, 1000);
          return;
        }

        // All information is now complete
        setUploadedResumeData(updatedData);
        resumeDataRef.current = updatedResumeForGeneration;
        setResumeData(updatedResumeForGeneration);

        setTimeout(() => {
          soundManager.resumeComplete();
          addMessage("Perfect! Now I have all the required information from your resume.", false);

          setTimeout(() => {
            addMessage("Let me optimize it with professional language and ATS keywords.", false);
            setIsLoading(false);
            optimizeResumeAndShowTemplate(updatedResumeForGeneration);
          }, 700);
        }, 500);
        return;
      }

      // Check if this is a follow-up after resume upload (post-upload step)
      const isPostUploadStep = currentStep === 'post-upload' && uploadedResumeData !== null;

      if (isPostUploadStep) {
        addMessage("Processing your updates...", false);

        const enhancementResponse = await supabase.functions.invoke('chat-with-ai', {
          body: {
            message: `The user uploaded their resume and now wants to add/update the following: "${userMessage}".

Current resume data: ${JSON.stringify(uploadedResumeData)}

Please acknowledge their input and confirm that you've noted their updates. Then let them know their resume is ready for download. Keep response brief (2-3 sentences).`,
            conversationHistory: messages.slice(-4).map(msg => ({
              role: msg.isUser ? 'user' : 'assistant',
              content: msg.content
            })),
            mode: 'collect'
          }
        });

        if (userMessage.toLowerCase().includes('continue') ||
          userMessage.toLowerCase().includes('no') ||
          userMessage.toLowerCase().includes("that's all") ||
          userMessage.toLowerCase().includes("looks good")) {
          setMessages(prev => prev.slice(0, -1));
          beginJobTailoring();
        } else {
          setMessages(prev => prev.slice(0, -1));
          const aiResponse = enhancementResponse?.data?.response ||
            "I've noted your updates.";
          addMessage(aiResponse, false);
          setTimeout(() => {
            beginJobTailoring();
          }, 600);
        }

        setIsLoading(false);
        return;
      }

      // If user is in greeting step and hasn't chosen a path yet
      if (currentStep === 'greeting' && !waitingForChoice && !showFormType) {
        const intent = detectInterest(userMessage);

        if (intent === 'create') {
          // User directly wants to create — show forms
          addMessage("Let's get started on your new resume!", false);
          setTimeout(() => {
            const formMessage: Message = {
              id: createMessageId(),
              content: "Please fill out your personal information below.",
              isUser: false,
              timestamp: new Date(),
              formType: 'personal'
            };
            setMessages(prev => [...prev, formMessage]);
            setShowFormType('personal');
            setCurrentStep('personal-info');
            setIsLoading(false);
          }, 800);
          return;
        }

        if (intent === 'upload') {
          addActionMessage("Sure - upload your PDF or DOCX resume and I'll read it for you.", 'uploadResume');
          setIsLoading(false);
          return;
        }

        if (intent === 'interested') {
          // User shows interest — show Create/Upload choice buttons
          addMessage("Let's do it! How would you like to proceed?", false);
          setWaitingForChoice(true);
          setIsLoading(false);
          return;
        }

        // Not interested — explain app and ask again
        const response = await callOpenAI(userMessage, false);
        addMessage(response, false);
        setIsLoading(false);
        return;
      }

      // Normal conversation flow (already in a flow)
      const shouldIncludeUploadData = uploadedResumeData !== null;
      const response = await callOpenAI(userMessage, shouldIncludeUploadData);

      // Only re-show the create/upload choice buttons in the greeting step.
      // Otherwise an AI message that mentions these phrases would wrongly
      // take the user back to the start.
      if (
        currentStep === 'greeting' &&
        (response.includes("Create a new resume") || response.includes("Upload a resume"))
      ) {
        setWaitingForChoice(true);
      }

      setTimeout(() => {
        addMessage(response, false);
        setIsLoading(false);
      }, 1000);

    } catch (error) {
      console.error("Error processing message:", error);
      addMessage("I apologize, but I encountered an error. Please try again.", false);
      setIsLoading(false);
    }
  }, [currentStep, addMessage, addActionMessage, beginJobTailoring, callOpenAI, uploadedResumeData, messages, waitingForChoice, showFormType, detectInterest, optimizeResumeAndShowTemplate]);


  const handleResumeUpload = useCallback(async (parsedData: any) => {
    setIsLoading(true);
    setIsUploadedResume(true);

    // Import validation function
    const { normalizeParsedResumeData, validateParsedResumeData } = await import('@/utils/validation');
    const normalizedParsedData = normalizeParsedResumeData(parsedData);
    setUploadedResumeData(normalizedParsedData);
    
    // Validate the parsed data
    const validation = validateParsedResumeData(normalizedParsedData);
    const resumeForGeneration = buildResumeDataFromParsed(normalizedParsedData, resumeDataRef.current);

    // Set resume data with whatever we have
    resumeDataRef.current = resumeForGeneration;
    setResumeData(resumeForGeneration);
    console.log('Setting resume data from upload:', resumeForGeneration);

    const parsedSkills = (normalizedParsedData.skills as any) || {};
    const hasSkillContent = (Array.isArray(parsedSkills.technical) && parsedSkills.technical.length > 0) ||
      (Array.isArray(parsedSkills.soft) && parsedSkills.soft.length > 0);
    const hasSupplementalResumeContent =
      (Array.isArray(normalizedParsedData.certifications) && normalizedParsedData.certifications.length > 0) ||
      (Array.isArray(normalizedParsedData.languages) && normalizedParsedData.languages.length > 0) ||
      (Array.isArray(normalizedParsedData.awards) && normalizedParsedData.awards.length > 0) ||
      (Array.isArray(normalizedParsedData.projects) && normalizedParsedData.projects.length > 0) ||
      (Array.isArray(normalizedParsedData.publications) && normalizedParsedData.publications.length > 0) ||
      (Array.isArray(normalizedParsedData.references) && normalizedParsedData.references.length > 0);
    const uploadAcceptedByParser = Boolean(
      normalizedParsedData._uploadAccepted ||
      normalizedParsedData._classification?.is_resume === true
    );
    const hasUsableResumeContent = uploadAcceptedByParser ||
      validation.isValid ||
      validation.hasWorkExperience ||
      validation.hasEducation ||
      hasSkillContent ||
      hasSupplementalResumeContent;

    if (!hasUsableResumeContent) {
      setMissingInfoFields([]);
      setCurrentStep('upload-prompt');
      setIsLoading(false);
      addActionMessage(
        "I couldn't read enough resume details from that file. Please try again with a clearer resume or CV file.",
        'uploadResume'
      );
      return false;
    }

    setMissingInfoFields([]);
    setCurrentStep('uploaded-resume-optimization');
    setTimeout(() => {
      soundManager.resumeComplete();
      addMessage("I've uploaded your resume successfully!", false);

      setTimeout(() => {
        addMessage("I've reviewed the resume content and found enough resume details. Let me optimize it with professional language and ATS keywords...", false);
        setIsLoading(false);
        optimizeResumeAndShowTemplate(resumeForGeneration);
      }, 700);
    }, 300);
    return true;

  }, [addActionMessage, addMessage, optimizeResumeAndShowTemplate]);

  const handleMissingInfoSubmit = useCallback(async (info: Record<string, string>) => {
    // Merge the provided info with existing resume data
    const { normalizeParsedResumeData } = await import('@/utils/validation');
    const supplementalInfo = Object.entries(info)
      .filter(([, value]) => typeof value === 'string' && value.trim().length > 0)
      .map(([field, value]) => `${field}: ${value.trim()}`)
      .join('\n');
    const updatedData = normalizeParsedResumeData({
      ...uploadedResumeData,
      ...info,
      additionalInfo: [uploadedResumeData?.additionalInfo, supplementalInfo].filter(Boolean).join('\n'),
    });
    const updatedResumeForGeneration: Partial<ResumeData> = {
      ...buildResumeDataFromParsed(updatedData, resumeDataRef.current),
      rawMissingInfo: info,
      additionalInfo: [resumeDataRef.current.additionalInfo, supplementalInfo].filter(Boolean).join('\n'),
    };
    setUploadedResumeData(updatedData);

    // Update the resume data with the new information
    resumeDataRef.current = updatedResumeForGeneration;
    setResumeData(updatedResumeForGeneration);

    // Clear missing info fields and proceed to optimization
    setMissingInfoFields([]);

    addMessage("Perfect! I've added your information. Let me optimize your resume with professional language and ATS optimization...", false);
    optimizeResumeAndShowTemplate(updatedResumeForGeneration);
  }, [uploadedResumeData, addMessage, optimizeResumeAndShowTemplate]);

  const handleFormSubmit = useCallback((formData: any, formType: 'personal' | 'work' | 'education') => {
    setIsLoading(true);

    let nextResumeData: Partial<ResumeData> = {};
    setResumeData(prev => {
      nextResumeData = mergeFormDataIntoResume(prev, formData, formType);
      resumeDataRef.current = nextResumeData;
      return nextResumeData;
    });

    addMessage("Sent", true);

    setTimeout(async () => {
      addMessage("Thinking...", false);

      setTimeout(async () => {
        let nextStep = '';
        let nextForm: 'personal' | 'work' | 'education' | null = null;

        if (formType === 'personal') {
          nextStep = "Great! I've received your personal information. Now let's gather your work experience.";
          nextForm = 'work';
        } else if (formType === 'work') {
          nextStep = "Excellent work experience! Now let's complete your education, skills, and additional information.";
          nextForm = 'education';
        } else if (formType === 'education') {
          if (!isOnline) {
            setIsLoading(true);
            addMessage("All information collected", false);

            setTimeout(() => {
              addMessage("You're offline, so AI enhancement and job tailoring are paused. Select a template to download your resume now; you can enhance or sync it when you're back online.", false);
              setShowTemplateSelection(true);
              setCurrentStep('template-selection');
              setIsLoading(false);
              setShowFormType(null);
            }, 800);
            return;
          }

          setIsLoading(true);
          addMessage("All information collected", false);

          setTimeout(() => {
            addMessage("Enhancing your resume with AI...", false);
            setTimeout(async () => {
              try {
                await generateResume(nextResumeData);
                setTimeout(() => {
                  soundManager.resumeComplete();
                  addMessage("Perfect! Your resume has been enhanced with professional language and action verbs.", false);
                  setTimeout(() => {
                    addMessage("Before we finalize, do you have a specific job you'd like to tailor this resume for?", false);
                    setShowJobDescriptionInput(true);
                    setCurrentStep('job-tailoring');
                    setIsLoading(false);
                    setShowFormType(null);
                  }, 1000);
                }, 1000);
              } catch (error) {
                console.error('Resume enhancement failed:', error);
                addMessage("Resume enhancement completed. You can proceed with download.", false);
                setTimeout(() => {
                  soundManager.resumeComplete();
                  addMessage("Before we finalize, do you have a specific job you'd like to tailor this resume for?", false);
                  setShowJobDescriptionInput(true);
                  setCurrentStep('job-tailoring');
                  setIsLoading(false);
                  setShowFormType(null);
                }, 1000);
              }
            }, 2000);
          }, 1500);
          return;
        }

        setMessages(prev => [...prev.slice(0, -1), {
          id: createMessageId(),
          content: nextStep,
          isUser: false,
          timestamp: new Date(),
          formType: nextForm || undefined
        }]);
        setShowFormType(nextForm);
        setIsLoading(false);
      }, 2000);
    }, 1000);
  }, [addMessage, generateResume, isOnline]);

  const handleJobDescriptionSubmit = useCallback(async (jobDescription: string) => {
    setAnalyzingJD(true);
    setShowJobDescriptionInput(false);
    setCurrentStep('job-description-analyzing');
    addMessage("Analyzing job description and tailoring your resume...", false);
    const latestResumeData = Object.keys(resumeDataRef.current || {}).length > 0
      ? resumeDataRef.current
      : resumeData;

    try {
      if (!isOnline) {
        throw new Error('Offline mode');
      }

      const { data, error } = await supabase.functions.invoke('analyze-job-description', {
        body: { jobDescription, resumeData: latestResumeData }
      });

      if (error) throw error;

      const analysis = normalizeATSAnalysis(data?.analysis);

      if (analysis) {
        const resumeWithAnalysis = {
          ...latestResumeData,
          targetJobDescription: jobDescription,
          atsAnalysis: analysis,
        };
        resumeDataRef.current = resumeWithAnalysis;
        setResumeData(resumeWithAnalysis);
        setAtsAnalysis(analysis);
        setMessages(prev => prev.filter((msg, index) => !(
          index === prev.length - 1 &&
          !msg.isUser &&
          msg.content === "Analyzing job description and tailoring your resume..."
        )));
        addMessage(`Analysis complete! Your resume has a ${analysis.score}% ATS compatibility score.`, false);
        setShowATSPreview(true);
        setShowTemplateSelection(false);
        setCurrentStep('ats-preview');
      } else {
        throw new Error('No ATS analysis returned');
      }
    } catch (error) {
      console.error('JD analysis error:', error);
      const fallbackAnalysis = createLocalATSAnalysis(jobDescription, latestResumeData);
      const resumeWithAnalysis = {
        ...latestResumeData,
        targetJobDescription: jobDescription,
        atsAnalysis: fallbackAnalysis,
      };
      resumeDataRef.current = resumeWithAnalysis;
      setResumeData(resumeWithAnalysis);
      setAtsAnalysis(fallbackAnalysis);
      setMessages(prev => prev.filter((msg, index) => !(
        index === prev.length - 1 &&
        !msg.isUser &&
        msg.content === "Analyzing job description and tailoring your resume..."
      )));
      addMessage(`Analysis complete! Your resume has a ${fallbackAnalysis.score}% ATS compatibility score.`, false);
      setShowATSPreview(true);
      setShowTemplateSelection(false);
      setCurrentStep('ats-preview');
    } finally {
      setAnalyzingJD(false);
    }
  }, [addMessage, isOnline, resumeData]);

  const handleSkipJobDescription = useCallback(() => {
    setShowJobDescriptionInput(false);
    addMessage("No problem! Let's select a template for your resume.", false);
    setShowTemplateSelection(true);
    setCurrentStep('template-selection');
  }, [addMessage]);

  const handleApplyImprovements = useCallback(async () => {
    const latestResumeData = Object.keys(resumeDataRef.current || {}).length > 0
      ? resumeDataRef.current
      : resumeData;
    const activeAnalysis = atsAnalysis || normalizeATSAnalysis((latestResumeData as any).atsAnalysis);
    const targetJobDescription = String((latestResumeData as any).targetJobDescription || '').trim();

    setShowATSPreview(false);
    setShowTemplateSelection(false);
    setCurrentStep('applying-ats-improvements');

    if (!activeAnalysis) {
      addMessage("I couldn't find ATS suggestions to apply. Let's continue to template selection.", false);
      setShowTemplateSelection(true);
      setCurrentStep('template-selection');
      return;
    }

    addMessage("Applying the ATS suggestions with AI so your resume is better tailored to the job...", false);

    if (!isOnline) {
      const tailoredResume = {
        ...latestResumeData,
        targetJobDescription,
        atsAnalysis: activeAnalysis,
        tailoringApplied: false,
      };

      resumeDataRef.current = tailoredResume;
      setResumeData(tailoredResume);
      addMessage("You're offline, so AI tailoring is paused. Your ATS analysis is saved; select a template to continue.", false);
      setShowTemplateSelection(true);
      setCurrentStep('template-selection');
      return;
    }

    const tailoringPayload = {
      ...latestResumeData,
      targetJobDescription,
      atsAnalysis: activeAnalysis,
      tailoringInstructions: {
        objective: 'Revise the enhanced resume so it better matches the target job while preserving the candidate’s true experience.',
        matchedKeywords: activeAnalysis.matchedKeywords,
        missingKeywords: activeAnalysis.missingKeywords,
        skillGaps: activeAnalysis.skillGaps,
        improvements: activeAnalysis.improvements,
        overallFeedback: activeAnalysis.overallFeedback,
      },
    };

    const wasTailored = await generateResume(tailoringPayload);
    const tailoredResume = {
      ...resumeDataRef.current,
      targetJobDescription,
      atsAnalysis: activeAnalysis,
      tailoringApplied: wasTailored,
    };

    resumeDataRef.current = tailoredResume;
    setResumeData(tailoredResume);

    addMessage(
      wasTailored
        ? "Done — I've applied the ATS suggestions and better aligned your resume with the job description. Now select a template to download."
        : "I couldn't fully apply the ATS suggestions automatically, but your analysis is saved. You can still select a template to continue.",
      false
    );
    setShowTemplateSelection(true);
    setCurrentStep('template-selection');
  }, [addMessage, atsAnalysis, generateResume, isOnline, resumeData]);

  const handleRejectImprovements = useCallback(() => {
    setShowATSPreview(false);
    addMessage("No changes made. Select a template to download your resume.", false);
    setShowTemplateSelection(true);
    setCurrentStep('template-selection');
  }, [addMessage]);

  const selectOption = useCallback((option: 'create' | 'upload') => {
    setWaitingForChoice(false);

    if (option === 'create') {
      addMessage("Create a new resume", true);
      setIsLoading(true);

      setTimeout(() => {
        const message: Message = {
          id: createMessageId(),
          content: "Let's start by filling out your personal information. Please complete the form below.",
          isUser: false,
          timestamp: new Date(),
          formType: 'personal'
        };
        setMessages(prev => [...prev, message]);
        setShowFormType('personal');
        setCurrentStep('personal-info');
        setIsLoading(false);
      }, 1000);
    } else {
      addMessage("Upload a resume", true);
      addActionMessage("Upload your PDF or DOCX resume below and I'll read it for you.", 'uploadResume');
      setCurrentStep('upload-prompt');
    }
  }, [addActionMessage, addMessage]);

  const editMessage = useCallback((messageId: string, newContent: string) => {
    setMessages(prev => prev.map(msg =>
      msg.id === messageId ? { ...msg, content: newContent } : msg
    ));
  }, []);

  const goBackToMessage = useCallback((messageId: string) => {
    setMessages(prev => {
      const messageIndex = prev.findIndex(msg => msg.id === messageId);
      if (messageIndex !== -1) {
        const rewoundMessages = prev.slice(0, messageIndex);
        const lastMessage = rewoundMessages[rewoundMessages.length - 1];

        setIsLoading(false);
        setIsConversationComplete(false);
        setShowTemplateSelection(false);
        setSelectedTemplateId(null);
        setShowJobDescriptionInput(false);
        setShowATSPreview(false);
        setAtsAnalysis(null);
        setMissingInfoFields([]);
        setRecommendedTemplate(null);

        if (!lastMessage || rewoundMessages.length <= 1) {
          setCurrentStep('greeting');
          setShowFormType(null);
          setWaitingForChoice(false);
        } else if (lastMessage.formType) {
          setCurrentStep(`${lastMessage.formType}-info`);
          setShowFormType(lastMessage.formType);
          setWaitingForChoice(false);
        } else {
          const isChoicePrompt = !lastMessage.isUser && lastMessage.content.includes('How would you like to proceed?');
          setCurrentStep('greeting');
          setShowFormType(null);
          setWaitingForChoice(isChoicePrompt);
        }

        return rewoundMessages;
      }
      return prev;
    });
  }, []);

  const refreshChat = useCallback(async () => {
    await clearChatSession();
    clearResumeBuilderFormDrafts();
    localStorage.setItem('resumeBuilder.currentStep', 'greeting');
    localStorage.setItem('resumeBuilder.showJobDescriptionInput', 'false');

    setMessages([
      {
        id: "welcome",
        content: `Hi ${displayName}, let's build you the perfect resume. Are you ready?`,
        isUser: false,
        timestamp: new Date(),
      },
    ]);
    setResumeData({});
    setCurrentStep("greeting");
    setIsConversationComplete(false);
    setUploadedResumeData(null);
    setShowTemplateSelection(false);
    setSelectedTemplateId(null);
    setIsUploadedResume(false);
    setShowJobDescriptionInput(false);
    setShowATSPreview(false);
    setAtsAnalysis(null);
    setWaitingForChoice(false);
    setShowFormType(null);
    setMissingInfoFields([]);
    setRecommendedTemplate(null);
    setIsLoading(false);
  }, [clearChatSession, displayName]);

  // Public helper used by Index.tsx so it can post AI messages without
  // having to reach into useChat's internal addMessage.
  const addAIMessage = useCallback((content: string, action?: Message['action']) => {
    if (action) {
      addActionMessage(content, action);
      return;
    }

    addMessage(content, false);
  }, [addActionMessage, addMessage]);

  // Re-show the create/upload choice buttons (used after a non-resume upload).
  const reopenUploadChoice = useCallback(() => {
    setWaitingForChoice(true);
    setCurrentStep('greeting');
  }, []);


  const handleTemplateSelect = useCallback((templateId: string) => {
    // Add loading message for template processing
    const loadingMessageId = createMessageId();
    setMessages(prev => [...prev, {
      id: loadingMessageId,
      content: "Preparing your resume template",
      isUser: false,
      timestamp: new Date(),
      isLoading: true
    }]);
    
    setSelectedTemplateId(templateId);
    setResumeData(prev => {
      const next = { ...prev, selectedTemplateId: templateId, templateId };
      resumeDataRef.current = next;
      return next;
    });
    setShowTemplateSelection(false);
    setIsConversationComplete(true);
    
    setTimeout(() => {
      setMessages(prev => prev.filter(msg => msg.id !== loadingMessageId));
      addMessage(`Template selected! Your resume is ready for download.`, false);
    }, 1500);
  }, [addMessage]);

  
  return {
    messages,
    isLoading,
    processUserMessage,
    generateResume,
    editMessage,
    goBackToMessage,
    refreshChat,
    resumeData,
    currentStep,
    isConversationComplete,
    showFormType,
    setShowFormType,
    handleFormSubmit,
    waitingForChoice,
    selectOption,
    handleResumeUpload,
    showTemplateSelection,
    selectedTemplateId,
    handleTemplateSelect,
    isUploadedResume,
    uploadedResumeData,
    showJobDescriptionInput,
    showATSPreview,
    atsAnalysis,
    analyzingJD,
    handleJobDescriptionSubmit,
    handleSkipJobDescription,
    handleApplyImprovements,
    handleRejectImprovements,
    addLoadingMessage,
    removeLoadingMessage,
    missingInfoFields,
    optimizeResumeAndShowTemplate,
    handleMissingInfoSubmit,
    addAIMessage,
    reopenUploadChoice,
    recommendedTemplate,
    setRecommendedTemplate,
  };
}
